import { useState } from 'react';
import { useCall } from '../../hooks/useCall';
import { useCallStore } from '../../stores/useCallStore';
import { useAuth } from '../../hooks/useAuth';
import IncomingCallModal from './IncomingCallModal';
import ActiveCallModal from './ActiveCallModal';

/**
 * Gelen/aktif çağrı modallarını yöneten izole bileşen.
 * Yüksek frekanslı çağrı state'ini (callDuration 1 Hz tick dahil) store
 * selector'larından okur; böylece MainLayout ve tüm sayfa ağacı arama
 * sırasında her saniye re-render olmaz — sadece bu modal ağacı güncellenir.
 */
export function CallModals() {
  const { user } = useAuth();
  const activeCall = useCallStore((s) => s.activeCall);
  const callStatus = useCallStore((s) => s.callStatus);
  const connectionState = useCallStore((s) => s.connectionState);
  const isMuted = useCallStore((s) => s.isMuted);
  const isVideoEnabled = useCallStore((s) => s.isVideoEnabled);
  const isSpeakerOn = useCallStore((s) => s.isSpeakerOn);
  const isLocalVideoLarge = useCallStore((s) => s.isLocalVideoLarge);
  const isBackCamera = useCallStore((s) => s.isBackCamera);
  const callDuration = useCallStore((s) => s.callDuration);
  const callError = useCallStore((s) => s.callError);
  const {
    localStream,
    remoteStream,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    toggleSpeaker,
    switchCamera,
    toggleLocalVideoSize,
    setRemoteAudioElement,
    clearCallError,
  } = useCall();
  const [acceptingCall, setAcceptingCall] = useState(false);
  const [rejectingCall, setRejectingCall] = useState(false);
  const [endingCall, setEndingCall] = useState(false);

  const showIncomingCall =
    callStatus === 'ringing' &&
    activeCall !== null &&
    activeCall.caller_id !== user?.id;

  const currentCall =
    activeCall && !(callStatus === 'ringing' && activeCall.caller_id !== user?.id)
      ? ({
          call: activeCall,
          remoteStream: remoteStream ?? undefined,
          isMuted,
          duration: callDuration,
        } as import('../../types').CallState)
      : null;

  return (
    <>
      <IncomingCallModal
        isOpen={showIncomingCall}
        call={activeCall}
        accepting={acceptingCall}
        rejecting={rejectingCall}
        onAccept={async () => {
          if (!activeCall) return;
          setAcceptingCall(true);
          try {
            await acceptCall(activeCall.id);
          } catch (err: unknown) {
            console.error(err);
          } finally {
            setAcceptingCall(false);
          }
        }}
        onReject={async () => {
          if (!activeCall) return;
          setRejectingCall(true);
          try {
            await rejectCall(activeCall.id);
          } catch (err: unknown) {
            console.error(err);
          } finally {
            setRejectingCall(false);
          }
        }}
      />

      <ActiveCallModal
        currentCall={currentCall}
        localStream={localStream}
        isVideoEnabled={isVideoEnabled}
        isLocalVideoLarge={isLocalVideoLarge}
        isBackCamera={isBackCamera}
        currentUserId={user?.id}
        connectionState={connectionState}
        callError={callError}
        isSpeakerOn={isSpeakerOn}
        ending={endingCall}
        onEnd={async () => {
          if (!activeCall) return;
          setEndingCall(true);
          try {
            await endCall(activeCall.id);
          } catch (err: unknown) {
            console.error(err);
          } finally {
            setEndingCall(false);
          }
        }}
        onToggleMute={toggleMute}
        onToggleVideo={toggleVideo}
        onToggleSpeaker={toggleSpeaker}
        onToggleLocalVideoSize={toggleLocalVideoSize}
        onSwitchCamera={switchCamera}
        onClearCallError={clearCallError}
        setRemoteAudioElement={setRemoteAudioElement}
      />
    </>
  );
}
